import React, { createContext, useContext } from "react";
import useElementSize from "./useElementSize";

/**
 * Context providing { width, height } of the chart region.
 * Consumed by card components that need explicit pixel dimensions (e.g. Recharts wrappers).
 */
export const BoardSizingContext = createContext({ width: 0, height: 0 });
export const useBoardSizing = () => useContext(BoardSizingContext);

/**
 * Fill-based chart region: takes all remaining vertical space in the card shell.
 *
 * Usage — render prop (explicit dimensions):
 *   <BoardChartRegion>
 *     {({ width, height }) => <InsightsChart height={height} ... />}
 *   </BoardChartRegion>
 *
 * Usage — context consumer:
 *   <BoardChartRegion>
 *     <MyChart />   // calls useBoardSizing() inside
 *   </BoardChartRegion>
 *
 * The outer div is flex-1 min-h-0, meaning it fills whatever space the shell
 * flex column leaves after the header. The ResizeObserver then measures it.
 */
export default function BoardChartRegion({ children, className = "", style }) {
  const { ref, width, height } = useElementSize();

  return (
    <div
      ref={ref}
      className={`flex-1 min-h-0 w-full relative overflow-hidden ${className}`}
      style={style}
    >
      {typeof children === "function" ? (
        children({ width, height })
      ) : (
        <BoardSizingContext.Provider value={{ width, height }}>
          {children}
        </BoardSizingContext.Provider>
      )}
    </div>
  );
}
